'use client';

/**
 * useSyncBus - Client-side hook for the unified SSE sync bus.
 *
 * Opens a single EventSource connection per opportunity and provides:
 * - Real-time event reception with typed handlers
 * - Command sending via REST POST
 * - Auto-reconnect with exponential backoff + jitter
 * - State snapshot on (re)connect
 * - Connection health tracking
 * - Graceful degradation detection
 */

import {
  useEffect,
  useRef,
  useCallback,
  useState,
  createContext,
  useContext,
} from 'react';
import type {
  SyncEventType,
  SyncEventPayloadMap,
  SyncSnapshot,
  SyncConnectionState,
  SyncRole,
} from '@/lib/types/sync-events';
import { captureClientPosthogEvent } from '@/lib/utils/capture-client-posthog';

// ============================================================================
// Constants
// ============================================================================

const BASE_RECONNECT_MS = 1000;
const MAX_RECONNECT_MS = 8_000;
const JITTER_FACTOR = 0.2;
const MAX_FAILURES_FOR_DEGRADED = 3;
const DEGRADED_RETRY_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const STALE_TIMEOUT_MS = 45_000;
const DEDUP_BUFFER_SIZE = 100;

// ============================================================================
// Types
// ============================================================================

export type EventHandler<T extends SyncEventType = SyncEventType> = (
  payload: SyncEventPayloadMap[T],
  sender: SyncRole,
) => void;

export interface SyncBusHandle {
  isConnected: boolean;
  isDegraded: boolean;
  connectionState: SyncConnectionState;
  lastSnapshot: SyncSnapshot | null;
  sendCommand: <T extends SyncEventType>(type: T, payload: SyncEventPayloadMap[T]) => Promise<void>;
  addHandler: <T extends SyncEventType>(type: T, handler: EventHandler<T>) => () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useSyncBus(
  opportunityId: string | undefined,
  role: SyncRole,
): SyncBusHandle {
  const [connectionState, setConnectionState] = useState<SyncConnectionState>('connecting');
  const [lastSnapshot, setLastSnapshot] = useState<SyncSnapshot | null>(null);

  const esRef = useRef<EventSource | null>(null);
  const handlersRef = useRef<Map<SyncEventType, Set<EventHandler>>>(new Map());
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEventTimeRef = useRef(Date.now());
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const staleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const seenIdsRef = useRef<Set<number>>(new Set());
  const failureCountRef = useRef(0);
  const mountedRef = useRef(true);

  // ---- Event handler registration ----

  const addHandler = useCallback(<T extends SyncEventType>(
    type: T,
    handler: EventHandler<T>,
  ): (() => void) => {
    if (!handlersRef.current.has(type)) {
      handlersRef.current.set(type, new Set());
    }
    handlersRef.current.get(type)!.add(handler as EventHandler);
    return () => {
      handlersRef.current.get(type)?.delete(handler as EventHandler);
    };
  }, []);

  // ---- Dispatch event to registered handlers ----

  const dispatch = useCallback((type: SyncEventType, payload: unknown, sender: SyncRole) => {
    const handlers = handlersRef.current.get(type);
    if (!handlers) return;
    for (const handler of handlers) {
      try {
        handler(payload as SyncEventPayloadMap[typeof type], sender);
      } catch (err) {
        console.error('[SyncBus] Handler error:', type, err);
      }
    }
  }, []);

  // ---- Send command via REST ----

  const sendCommand = useCallback(async <T extends SyncEventType>(
    type: T,
    payload: SyncEventPayloadMap[T],
  ): Promise<void> => {
    if (!opportunityId) return;
    try {
      const res = await fetch('/api/sync/command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ opportunityId, type, payload, role }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error('[SyncBus] Command failed:', type, data);
      }
    } catch (err) {
      console.error('[SyncBus] Command error:', type, err);
    }
  }, [opportunityId, role]);

  // ---- SSE Connection lifecycle ----

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);

    const attempt = reconnectAttemptRef.current;
    const baseDelay = Math.min(BASE_RECONNECT_MS * Math.pow(2, attempt), MAX_RECONNECT_MS);
    const jitter = baseDelay * JITTER_FACTOR * (Math.random() * 2 - 1);
    const delay = Math.max(500, baseDelay + jitter);

    reconnectAttemptRef.current = attempt + 1;

    reconnectTimerRef.current = setTimeout(() => {
      if (mountedRef.current) connectRef.current();
    }, delay);
  }, []);

  const connectRef = useRef<() => void>(() => {});

  connectRef.current = () => {
    if (!opportunityId || !mountedRef.current) return;

    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    const url = `/api/sync/stream?opportunityId=${encodeURIComponent(opportunityId)}&role=${role}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener('snapshot', (e: MessageEvent) => {
      if (!mountedRef.current) return;
      try {
        const data = JSON.parse(e.data);
        setLastSnapshot(data.payload as SyncSnapshot);
      } catch {
        // Ignore malformed snapshots
      }
      lastEventTimeRef.current = Date.now();
    });

    es.addEventListener('sync', (e: MessageEvent) => {
      if (!mountedRef.current) return;
      lastEventTimeRef.current = Date.now();

      try {
        const data = JSON.parse(e.data);
        const eventId = Number(e.lastEventId);

        if (eventId && seenIdsRef.current.has(eventId)) return;
        if (eventId) {
          seenIdsRef.current.add(eventId);
          if (seenIdsRef.current.size > DEDUP_BUFFER_SIZE) {
            const arr = Array.from(seenIdsRef.current);
            seenIdsRef.current = new Set(arr.slice(-DEDUP_BUFFER_SIZE / 2));
          }
        }

        dispatch(data.type as SyncEventType, data.payload, data.sender);
      } catch {
        // Ignore malformed events
      }
    });

    es.addEventListener('flags', (e: MessageEvent) => {
      if (!mountedRef.current) return;
      lastEventTimeRef.current = Date.now();
      try {
        const data = JSON.parse(e.data);
        dispatch('flags' as SyncEventType, data.payload, 'rep');
      } catch {
        // Ignore malformed flags
      }
    });

    es.onopen = () => {
      if (!mountedRef.current) return;
      setConnectionState('connected');
      reconnectAttemptRef.current = 0;
      failureCountRef.current = 0;

      captureClientPosthogEvent('sync_bus_connected', { role, opportunityId });
    };

    es.onerror = () => {
      if (!mountedRef.current) return;
      es.close();
      esRef.current = null;

      failureCountRef.current++;

      captureClientPosthogEvent('sync_bus_disconnected', {
        role,
        opportunityId,
        failureCount: failureCountRef.current,
      });

      if (failureCountRef.current >= MAX_FAILURES_FOR_DEGRADED) {
        setConnectionState('degraded');
        captureClientPosthogEvent('sync_bus_degraded', { role, opportunityId });
        // Schedule a slow retry instead of giving up permanently
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current) connectRef.current();
        }, DEGRADED_RETRY_MS);
        return;
      }

      setConnectionState('reconnecting');
      scheduleReconnect();
    };
  };

  // ---- Heartbeat ----

  useEffect(() => {
    if (!opportunityId || connectionState !== 'connected') return;

    heartbeatTimerRef.current = setInterval(() => {
      sendCommand('heartbeat', { role, timestamp: Date.now() }).catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    };
  }, [opportunityId, connectionState, role, sendCommand]);

  // ---- Stale detection ----

  useEffect(() => {
    if (connectionState !== 'connected') return;

    staleTimerRef.current = setInterval(() => {
      if (Date.now() - lastEventTimeRef.current > STALE_TIMEOUT_MS) {
        if (esRef.current) {
          esRef.current.close();
          esRef.current = null;
        }
        setConnectionState('reconnecting');
        scheduleReconnect();
      }
    }, 10_000);

    return () => {
      if (staleTimerRef.current) clearInterval(staleTimerRef.current);
    };
  }, [connectionState, scheduleReconnect]);

  // ---- Degraded recovery ----

  useEffect(() => {
    if (connectionState !== 'degraded' || !opportunityId) return;

    const interval = setInterval(() => {
      if (mountedRef.current && !esRef.current) {
        connectRef.current();
      }
    }, DEGRADED_RETRY_MS);

    return () => clearInterval(interval);
  }, [connectionState, opportunityId]);

  // ---- Main connection ----

  useEffect(() => {
    mountedRef.current = true;
    if (opportunityId) connectRef.current();

    return () => {
      mountedRef.current = false;
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      if (staleTimerRef.current) clearInterval(staleTimerRef.current);
    };
  }, [opportunityId, role]);

  return {
    isConnected: connectionState === 'connected',
    isDegraded: connectionState === 'degraded',
    connectionState,
    lastSnapshot,
    sendCommand,
    addHandler,
  };
}

// ============================================================================
// Context for descendant components
// ============================================================================

const SyncBusContext = createContext<SyncBusHandle | null>(null);

export { SyncBusContext };

/**
 * Access the sync bus from context. Must be inside a SyncBusProvider.
 */
export function useSyncBusContext(): SyncBusHandle {
  const ctx = useContext(SyncBusContext);
  if (!ctx) throw new Error('useSyncBusContext must be used within SyncBusProvider');
  return ctx;
}

/**
 * Register a handler for a sync event type. Cleans up on unmount.
 * Must be called inside a SyncBusProvider.
 */
export function useOnSyncEvent<T extends SyncEventType>(
  type: T,
  handler: EventHandler<T>,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const { addHandler } = useSyncBusContext();

  useEffect(() => {
    const stableHandler: EventHandler<T> = (payload, sender) => {
      handlerRef.current(payload, sender);
    };
    return addHandler(type, stableHandler);
  }, [type, addHandler]);
}
